import { useRef, useState } from 'react';
import { UploadCloud, Loader2 } from 'lucide-react';
import { getLabTextFileError } from './labTextFileInput';

export function ReviewsFileInput({ value, onChange, allowDrop = true }: {
  value: string;
  onChange: (value: string) => void;
  allowDrop?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const readFiles = async (files: File[]) => {
    if (!files.length || busy) return;
    setError('');
    setBusy(true);
    try {
      const texts: string[] = [];
      if (files.reduce((size, file) => size + file.size, new Blob([value]).size) > 2 * 1024 * 1024) {
        throw new Error('Opinie mogą mieć łącznie maksymalnie 2 MB.');
      }
      for (const file of files) {
        if (!/\.(csv|txt|md)$/i.test(file.name)) throw new Error('Wybierz pliki CSV, TXT lub MD.');
        const text = await file.text();
        const fileError = getLabTextFileError(file.name, text);
        if (fileError) throw new Error(fileError);
        if (!text.trim()) throw new Error(`Plik ${file.name} jest pusty.`);
        texts.push(text);
      }
      onChange([value, ...texts].filter(text => text.trim()).join('\n\n'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nie udało się odczytać opinii.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return <div className="space-y-2">
    <div onDragOver={event => { event.preventDefault(); if (allowDrop && !busy) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={event => { event.preventDefault(); setDragging(false); if (allowDrop) void readFiles(Array.from(event.dataTransfer.files)); }}
      className={`rounded-xl border border-dashed p-4 text-center ${dragging ? 'border-accent-primary bg-accent-primary/10' : 'border-border bg-surface-hover/30'}`}>
      <input ref={input} type="file" multiple accept=".csv,.txt,.md" className="sr-only" aria-label="Pliki z opiniami" disabled={busy}
        onChange={event => { void readFiles(Array.from(event.target.files || [])); }} />
      <button type="button" disabled={busy} onClick={() => input.current?.click()} className="inline-flex items-center gap-2 text-sm font-medium text-accent-primary disabled:opacity-50">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}
        {busy ? 'Wczytuję opinie...' : 'Wybierz pliki z opiniami'}
      </button>
      <p className="mt-2 text-xs text-text-muted">{allowDrop ? 'Przeciągnij tutaj pliki lub wybierz je z dysku. ' : ''}CSV, TXT lub MD, łącznie do 2 MB.</p>
      {value && <p className="mt-2 text-xs text-success">Wczytano {value.length.toLocaleString('pl-PL')} znaków. Nowe pliki zostaną dodane do obecnych opinii.</p>}
    </div>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    {value && <details className="text-xs text-text-secondary"><summary className="cursor-pointer">Podgląd opinii</summary><pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap">{value}</pre></details>}
  </div>;
}
